import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
} from "@mui/material";
import {
  PeopleOutlined,
  EventNoteOutlined,
  CheckCircleOutlined,
  PendingOutlined,
  NotificationsOutlined,
} from "@mui/icons-material";
import { selectUser } from "../../../store/slices/authSlice";
import { ROUTES } from "../../../constants";

// Import APIs
import { employeesAPI } from "../../../services/api/employees.api";
import { leavesAPI } from "../../../services/api/leaves.api";
import activityLogsAPI from "../../../services/api/activityLogs.api";
import assetsAPI from "../../../services/api/assets.api";
import { budgetAPI } from "../../../services/api/budget.api";

// Minimalistic stat card component
const StatCard = ({ title, value, icon, loading }) => (
  <Card 
    elevation={0}
    sx={{ 
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      height: '100%',
      transition: 'all 0.2s',
      '&:hover': {
        borderColor: 'primary.main',
        boxShadow: 1,
      }
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon && (
            <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
              {icon}
            </Box>
          )}
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {loading ? <CircularProgress size={24} /> : value}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  // Fetch comprehensive data for admin
  const { data: employeesData, isLoading: employeesLoading } = useQuery({
    queryKey: ["employees", "admin-dashboard"],
    queryFn: () => employeesAPI.getAll({ limit: 1000 }),
    staleTime: 5 * 60 * 1000,
  });

  const { data: leavesData, isLoading: leavesLoading } = useQuery({
    queryKey: ["leaves", "admin-dashboard"],
    queryFn: () => leavesAPI.getAll({ status: "pending", limit: 1000 }),
    staleTime: 2 * 60 * 1000,
  });

  const { data: assetsData, isLoading: assetsLoading } = useQuery({
    queryKey: ["assets", "admin-dashboard"],
    queryFn: () => assetsAPI.getStatistics(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: budgetData, isLoading: budgetLoading } = useQuery({
    queryKey: ["budgets", "admin-dashboard"],
    queryFn: () => budgetAPI.getBudgets({ limit: 1000 }),
    staleTime: 5 * 60 * 1000,
  });

  const { data: activitiesData, isLoading: activitiesLoading } = useQuery({
    queryKey: ["activities", "admin-dashboard"],
    queryFn: () => activityLogsAPI.getRecentActivities(10),
    staleTime: 1 * 60 * 1000,
  });

  // Fetch approval statistics
  const { data: approvalStatsData, isLoading: approvalStatsLoading } = useQuery({
    queryKey: ["approval-statistics", "admin-dashboard"],
    queryFn: () => leavesAPI.getApprovalStatistics(),
    staleTime: 2 * 60 * 1000,
  });

  // Calculate stats - Fix data extraction
  // API service returns response.data which is: { success: true, message: "...", data: { ... } }
  const totalEmployees =
    employeesData?.data?.pagination?.totalItems ||
    employeesData?.data?.total ||
    (Array.isArray(employeesData?.data?.users) ? employeesData.data.users.length : 0) ||
    (Array.isArray(employeesData?.data?.data?.users) ? employeesData.data.data.users.length : 0) ||
    (Array.isArray(employeesData?.data?.data) ? employeesData.data.data.length : 0) ||
    (Array.isArray(employeesData?.data) ? employeesData.data.length : 0) ||
    0;
  
  // Fix pending leaves count - handle different response structures
  let pendingLeaves = 0;
  if (leavesData) {
    if (leavesData.data?.pagination?.totalItems) {
      pendingLeaves = leavesData.data.pagination.totalItems;
    } else if (leavesData.data?.data?.pagination?.totalItems) {
      pendingLeaves = leavesData.data.data.pagination.totalItems;
    } else if (leavesData.data?.total) {
      pendingLeaves = leavesData.data.total;
    } else if (Array.isArray(leavesData.data?.applications)) {
      pendingLeaves = leavesData.data.applications.length;
    } else if (Array.isArray(leavesData.data?.data?.applications)) {
      pendingLeaves = leavesData.data.data.applications.length;
    } else if (Array.isArray(leavesData.data)) {
      pendingLeaves = leavesData.data.length;
    } else if (Array.isArray(leavesData.applications)) {
      pendingLeaves = leavesData.applications.length;
    }
  }
  const totalAssets =
    assetsData?.data?.categoryStats?.reduce(
      (sum, category) => sum + (category.count || 0),
      0
    ) || 0;
  const totalBudget =
    budgetData?.data?.budgets?.length ||
    budgetData?.data?.pagination?.totalItems ||
    0;
  const recentActivities = activitiesData?.data || [];
  const approvalStats = approvalStatsData?.data || { pending: 0, approved: 0, rejected: 0 };

  const getUserGreeting = () => {
    const hour = new Date().getHours();
    let greeting = "Good morning";
    if (hour >= 12 && hour < 17) greeting = "Good afternoon";
    else if (hour >= 17) greeting = "Good evening";
    const firstName = user?.first_name || user?.name || "Admin";
    return `${greeting}, ${firstName}!`;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Welcome Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
          {getUserGreeting()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Welcome to the Admin Dashboard. Here's an overview of your organization.
        </Typography>
      </Box>

      {/* Key Statistics */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Employees"
            value={totalEmployees}
            icon={<PeopleOutlined sx={{ fontSize: 20 }} />}
            loading={employeesLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Leaves"
            value={pendingLeaves}
            icon={<EventNoteOutlined sx={{ fontSize: 20 }} />}
            loading={leavesLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Assets"
            value={totalAssets}
            icon={<EventNoteOutlined sx={{ fontSize: 20 }} />}
            loading={assetsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Budgets"
            value={totalBudget}
            icon={<EventNoteOutlined sx={{ fontSize: 20 }} />}
            loading={budgetLoading}
          />
        </Grid>
      </Grid>

      {/* Approval Statistics */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                My Approval Statistics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="h3" sx={{ fontWeight: 600, color: 'success.main', mb: 0.5 }}>
                      {approvalStatsLoading ? <CircularProgress size={32} /> : approvalStats.approved}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Approved
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="h3" sx={{ fontWeight: 600, color: 'error.main', mb: 0.5 }}>
                      {approvalStatsLoading ? <CircularProgress size={32} /> : approvalStats.rejected}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Rejected
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="h3" sx={{ fontWeight: 600, color: 'warning.main', mb: 0.5 }}>
                      {approvalStatsLoading ? <CircularProgress size={32} /> : approvalStats.pending}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pending
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(ROUTES.LEAVE_APPROVALS || "/leaves/approvals")}
                >
                  View All Approvals
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Recent System Activity
              </Typography>
              {activitiesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : recentActivities.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No recent activities
                </Typography>
              ) : (
                <List sx={{ p: 0 }}>
                  {recentActivities.slice(0, 5).map((activity, index) => (
                    <React.Fragment key={activity.id || index}>
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'action.hover' }}>
                            <NotificationsOutlined sx={{ fontSize: 18 }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {activity.action || "System Activity"}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {activity.description || "Activity performed"} • {new Date(activity.created_at).toLocaleString()}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < recentActivities.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                System Overview
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Total Employees
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {totalEmployees}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Pending Leaves
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {pendingLeaves}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Total Assets
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {totalAssets}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
